import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";

const Profile = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    address: "",
    password: "",
  });

  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch user data when the component mounts
  useEffect(() => {
    if (user?.id) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/get-user/${user.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          setFormData({
            name: res.data.name || "",
            email: res.data.email || "",
            contact: res.data.contact || "",
            address: res.data.address || "",
            password: "",
            profile_pic: res.data.profile_pic || "",
          });
          setPreviewImage(res.data.profileImage);
        })
        .catch((err) => {
          console.error("Error fetching user:", err);
        });
    }
  }, [user?.id, token]); // Run only if user ID or token changes

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Handle form submission (update profile)
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true); // Show loading spinner while submitting

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("contact", formData.contact);
    data.append("address", user?.isDoctor === 1 ? "NA" : formData.address);
    data.append("password", formData.password);

    if (profileImage) {
      // Use the correct field name 'profile_pic' as expected by the backend
      data.append("profile_pic", profileImage);
    }

    axios
      .put(`${process.env.REACT_APP_API_URL}/update-user/${user.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        alert("Profile updated successfully ✅");
        setLoading(false);
      })
      .catch((err) => {
        alert("Profile update failed ❌");
        console.error(err);
        setLoading(false);
      });
  };

  return (
    <Box
      sx={{
        mt: "50px",
        minHeight: "calc(100vh - 80px)",
        backgroundColor: "#f3f6fc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 5,
          m: 10,
          width: "100%",
          borderRadius: 3,
          boxShadow: 5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            py: 2,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            boxShadow: 2,
            backgroundColor: "aliceblue",
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="primary">
            My Profile
          </Typography>
          <a
            className="btn btn-sm btn-outline-success fw-bold"
            href="/my-booking"
            style={{ textDecoration: "none" }}
          >
            My Appointments
          </a>
        </Box>

        {/* Main content below header */}
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Profile Image */}
            <Grid item xs={12} md={4} textAlign="center">
              <Avatar
                src={
                  previewImage ||
                  `${process.env.REACT_APP_API_URL}/${formData.profile_pic}`
                }
                sx={{
                  width: 300,
                  height: 300,
                  mx: "auto",
                  boxShadow: 2,
                  "& img": {
                    objectFit: "contain",
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                sx={{ marginTop: 10, fontWeight: "bold" }}
              >
                Upload
                <input type="file" hidden onChange={handleImageChange} />
              </Button>
            </Grid>

            {/* Details Form */}
            <Grid item xs={12} md={8}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                      style={{
                        marginBottom: 50,
                        marginTop: 20,
                        backgroundColor: "aliceblue",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                      style={{
                        marginBottom: 50,
                        marginTop: 20,
                        backgroundColor: "aliceblue",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contact"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon />
                          </InputAdornment>
                        ),
                      }}
                      style={{ marginBottom: 50, backgroundColor: "aliceblue" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {user?.isDoctor !== 1 && (
                      <TextField
                        label="Address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <HomeIcon />
                            </InputAdornment>
                          ),
                        }}
                        style={{
                          marginBottom: 50,
                          backgroundColor: "aliceblue",
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      fullWidth
                      placeholder="Leave blank if unchanged"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                      style={{ marginBottom: 20, backgroundColor: "aliceblue" }}
                    />
                  </Grid>
                  <Grid item xs={12} textAlign="right">
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        fontWeight: "bold",
                        mt: 2,
                        width: 200,
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
